/**
 * ActionBarHUD - Bottom-center 7~8 slot skill bar (8 slots for Monk!)
 * Displays real-time Skill Level (Trained + Enchanter +10 Cap) & Scaled Costs.
 * Includes Rich RPG Skill Tooltips with per-level growth summary & Down-ranking info.
 */

import { EntityState, DamageType } from '../../core/Types';
import { CLASS_SKILL_DEFINITIONS, getScaledSkillMetrics } from '../combat/CombatTypes';
import { getEffectiveSkillLevel, getDerivedCombatMetrics } from '../character/CharacterSystem';

interface SlotElements {
  slotEl: HTMLElement;
  iconEl: HTMLElement;
  nameEl: HTMLElement;
  costEl: HTMLElement;
  lvlBadgeEl: HTMLElement;
  cdOverlayEl: HTMLElement;
}

export class ActionBarHUD {
  private containerEl: HTMLElement | null = null;
  private onTriggerSkill: (slot: number) => void;
  private cachedSlots: Record<number, SlotElements> = {};
  private currentRole: string | null = null;

  // Tooltip UI state
  private tooltipEl: HTMLElement | null = null;
  private tooltipArrowEl: HTMLElement | null = null;
  private activeTooltipSlot: number | null = null;
  private isHoveredSlot: number | null = null;
  private isHeldSlot: number | null = null;
  private isPinnedSlot: number | null = null;
  private lastPlayerState: EntityState | null = null;

  constructor(onTriggerSkill: (slot: number) => void) {
    this.onTriggerSkill = onTriggerSkill;
  }

  public mount(parent: HTMLElement): void {
    const el = document.createElement('div');
    el.id = 'action-bar-hud';
    el.style.position = 'absolute';
    el.style.bottom = '16px';
    el.style.left = '50%';
    el.style.transform = 'translateX(-50%)';
    el.style.display = 'flex';
    el.style.gap = '8px';
    el.style.backgroundColor = 'rgba(13, 17, 23, 0.92)';
    el.style.border = '1px solid #30363d';
    el.style.borderRadius = '12px';
    el.style.padding = '8px 12px';
    el.style.boxShadow = '0 10px 30px rgba(0,0,0,0.6)';
    el.style.backdropFilter = 'blur(6px)';
    el.style.zIndex = '50';
    el.style.userSelect = 'none';

    parent.appendChild(el);
    this.containerEl = el;

    this.createTooltipElement();

    if (typeof window !== 'undefined') {
      window.addEventListener('mouseup', () => {
        this.isHeldSlot = null;
        this.checkHideTooltip();
      });

      window.addEventListener('click', (e) => {
        if (!this.containerEl?.contains(e.target as Node)) {
          this.isPinnedSlot = null;
          this.checkHideTooltip();
        }
      });
    }

    this.initSlotElements();
  }

  private createTooltipElement(): void {
    if (!this.containerEl) return;

    const tip = document.createElement('div');
    tip.id = 'action-bar-skill-tooltip';
    tip.style.position = 'absolute';
    tip.style.bottom = 'calc(100% + 14px)';
    tip.style.width = '330px';
    tip.style.backgroundColor = 'rgba(15, 23, 42, 0.97)';
    tip.style.border = '1.5px solid #3b82f6';
    tip.style.borderRadius = '10px';
    tip.style.padding = '12px 14px';
    tip.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.8), 0 0 15px rgba(59, 130, 246, 0.25)';
    tip.style.backdropFilter = 'blur(10px)';
    tip.style.color = '#f8fafc';
    tip.style.fontFamily = 'system-ui, -apple-system, sans-serif';
    tip.style.display = 'none';
    tip.style.pointerEvents = 'none';
    tip.style.zIndex = '100';

    const arrow = document.createElement('div');
    arrow.style.position = 'absolute';
    arrow.style.top = '100%';
    arrow.style.left = '50%';
    arrow.style.transform = 'translateX(-50%)';
    arrow.style.width = '0';
    arrow.style.height = '0';
    arrow.style.borderLeft = '7px solid transparent';
    arrow.style.borderRight = '7px solid transparent';
    arrow.style.borderTop = '8px solid #3b82f6';
    tip.appendChild(arrow);

    this.containerEl.appendChild(tip);
    this.tooltipEl = tip;
    this.tooltipArrowEl = arrow;
  }

  private initSlotElements(): void {
    if (!this.containerEl) return;
    this.cachedSlots = {};

    for (let slot = 1; slot <= 8; slot++) {
      const slotEl = document.createElement('div');
      slotEl.setAttribute('data-slot', slot.toString());
      slotEl.style.position = 'relative';
      slotEl.style.width = '58px';
      slotEl.style.height = '64px';
      slotEl.style.background = 'rgba(30, 41, 59, 0.7)';
      slotEl.style.border = '2px solid #475569';
      slotEl.style.borderRadius = '8px';
      slotEl.style.display = 'flex';
      slotEl.style.flexDirection = 'column';
      slotEl.style.alignItems = 'center';
      slotEl.style.justifyContent = 'space-between';
      slotEl.style.padding = '4px 2px';
      slotEl.style.cursor = 'pointer';
      slotEl.style.transition = 'border-color 0.15s, transform 0.08s';

      const keyEl = document.createElement('span');
      keyEl.style.position = 'absolute';
      keyEl.style.top = '2px';
      keyEl.style.left = '4px';
      keyEl.style.fontSize = '10px';
      keyEl.style.fontWeight = 'bold';
      keyEl.style.color = '#f59e0b';
      keyEl.innerText = slot.toString();
      slotEl.appendChild(keyEl);

      const lvlBadgeEl = document.createElement('span');
      lvlBadgeEl.style.position = 'absolute';
      lvlBadgeEl.style.bottom = '16px';
      lvlBadgeEl.style.right = '2px';
      lvlBadgeEl.style.fontSize = '9px';
      lvlBadgeEl.style.fontWeight = 'bold';
      lvlBadgeEl.style.background = 'rgba(15, 23, 42, 0.85)';
      lvlBadgeEl.style.color = '#38bdf8';
      lvlBadgeEl.style.padding = '0px 3px';
      lvlBadgeEl.style.borderRadius = '3px';
      lvlBadgeEl.style.border = '1px solid rgba(56,189,248,0.4)';
      slotEl.appendChild(lvlBadgeEl);

      const costEl = document.createElement('div');
      costEl.style.fontSize = '9px';
      costEl.style.fontWeight = 'bold';
      costEl.style.lineHeight = '1';
      costEl.style.marginTop = '2px';
      costEl.style.alignSelf = 'flex-end';
      costEl.style.marginRight = '2px';
      slotEl.appendChild(costEl);

      const iconEl = document.createElement('span');
      iconEl.style.fontSize = '20px';
      iconEl.style.lineHeight = '1';
      slotEl.appendChild(iconEl);

      const nameEl = document.createElement('span');
      nameEl.style.fontSize = '9px';
      nameEl.style.fontWeight = '600';
      nameEl.style.color = '#cbd5e1';
      nameEl.style.textAlign = 'center';
      nameEl.style.whiteSpace = 'nowrap';
      nameEl.style.overflow = 'hidden';
      nameEl.style.textOverflow = 'ellipsis';
      nameEl.style.maxWidth = '54px';
      slotEl.appendChild(nameEl);

      const cdOverlayEl = document.createElement('div');
      cdOverlayEl.style.position = 'absolute';
      cdOverlayEl.style.inset = '0';
      cdOverlayEl.style.background = 'rgba(0,0,0,0.72)';
      cdOverlayEl.style.display = 'none';
      cdOverlayEl.style.alignItems = 'center';
      cdOverlayEl.style.justifyContent = 'center';
      cdOverlayEl.style.fontSize = '13px';
      cdOverlayEl.style.fontWeight = 'bold';
      cdOverlayEl.style.color = '#f87171';
      cdOverlayEl.style.borderRadius = '6px';
      slotEl.appendChild(cdOverlayEl);

      slotEl.addEventListener('click', (e) => {
        if (e.button === 0) {
          this.onTriggerSkill(slot);
        }
      });

      slotEl.addEventListener('mouseenter', () => {
        this.isHoveredSlot = slot;
        this.showSkillTooltip(slot);
      });
      slotEl.addEventListener('mouseleave', () => {
        this.isHoveredSlot = null;
        this.checkHideTooltip();
      });

      slotEl.addEventListener('mousedown', (e) => {
        if (e.button === 0) {
          this.isHeldSlot = slot;
          this.showSkillTooltip(slot);
        }
      });

      slotEl.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.isPinnedSlot === slot) {
          this.isPinnedSlot = null;
          this.checkHideTooltip();
        } else {
          this.isPinnedSlot = slot;
          this.showSkillTooltip(slot);
        }
      });

      this.containerEl.appendChild(slotEl);
      this.cachedSlots[slot] = { slotEl, iconEl, nameEl, costEl, lvlBadgeEl, cdOverlayEl };
    }
  }

  private showSkillTooltip(slot: number): void {
    if (!this.tooltipEl || !this.containerEl) return;
    const s = this.cachedSlots[slot];
    if (!s || s.slotEl.style.display === 'none') return;

    this.activeTooltipSlot = slot;

    const slotLeft = s.slotEl.offsetLeft;
    const slotWidth = s.slotEl.offsetWidth;
    const slotCenter = slotLeft + slotWidth / 2;
    const tooltipWidth = 330;
    const barWidth = this.containerEl.offsetWidth;

    const tooltipLeft = Math.max(0, Math.min(barWidth - tooltipWidth, slotCenter - tooltipWidth / 2));
    this.tooltipEl.style.left = `${tooltipLeft}px`;

    if (this.tooltipArrowEl) {
      const arrowX = slotCenter - tooltipLeft;
      this.tooltipArrowEl.style.left = `${arrowX}px`;
    }

    this.renderTooltipContent(slot);
    this.tooltipEl.style.display = 'block';
  }

  private checkHideTooltip(): void {
    if (this.isHoveredSlot === null && this.isHeldSlot === null && this.isPinnedSlot === null) {
      this.hideSkillTooltip();
    }
  }

  private hideSkillTooltip(): void {
    if (!this.tooltipEl) return;
    this.activeTooltipSlot = null;
    this.tooltipEl.style.display = 'none';
  }

  private getEffectiveSkillSet(player: EntityState): { skillSet: Record<number, any>; maxSlots: number; roleKey: string } {
    const isNovice = player.job?.includes('무직') || player.job?.includes('초보자') || player.job?.includes('성인식');
    const isAdmin =
      player.role === 'admin' ||
      (player as any).isAdmin === true ||
      (player.job && (player.job.includes('관리자') || player.job.includes('admin') || player.job.includes('국왕') || player.job.includes('Lord British'))) ||
      player.appearance?.isKing;

    if (isAdmin) {
      return { skillSet: CLASS_SKILL_DEFINITIONS.admin, maxSlots: 8, roleKey: 'admin' };
    }
    if (isNovice) {
      return { skillSet: CLASS_SKILL_DEFINITIONS.novice, maxSlots: 1, roleKey: 'novice' };
    }
    const maxSlots = player.role === 'monk_hybrid' ? 8 : 7;
    const skillSet = CLASS_SKILL_DEFINITIONS[player.role] || CLASS_SKILL_DEFINITIONS.tank;
    return { skillSet, maxSlots, roleKey: player.role };
  }

  private renderTooltipContent(slot: number): void {
    if (!this.tooltipEl || !this.lastPlayerState) return;

    const player = this.lastPlayerState;
    const { skillSet } = this.getEffectiveSkillSet(player);
    const def = skillSet[slot];
    if (!def) return;

    const trainedLv = player.specSkillLevels?.[slot] ?? 1;
    const castLv = player.selectedCastLevels?.[slot] ?? trainedLv;
    const equipBonus = Math.min(10, player.equipSkillBonuses?.[slot] ?? 0);
    const effLevel = getEffectiveSkillLevel(player, slot);
    const scaled = getScaledSkillMetrics(def, effLevel);
    const metrics = getDerivedCombatMetrics(player);
    const actualSp = Math.round(scaled.spCost * metrics.spCostMult);

    const cd = player.cooldowns[slot] || 0;
    const isToggleActive =
      def.isToggle && player.buffs.some((b) => b.id.includes(`_${player.role}_${slot}`));

    const damageTypeNames: Record<DamageType, string> = {
      slash: '슬래시(Slash)',
      thrust: '쓰러스트(Thrust)',
      crush: '크러시(Crush)',
      holy: '마법/신성(Holy)',
      pure: '순수(Pure)',
    };
    const dmgTypeKr = damageTypeNames[def.damageType as DamageType] || def.damageType;
    const powerText = scaled.power > 0 ? `${dmgTypeKr} 수치 ${scaled.power}` : `${dmgTypeKr} 제어/보조`;

    let costHtml = '';
    if (actualSp > 0) costHtml += `<span style="color:#eab308; font-weight:bold;">${actualSp} SP</span> `;
    if (scaled.mpCost > 0) costHtml += `<span style="color:#38bdf8; font-weight:bold;">${scaled.mpCost} MP</span> `;
    if (scaled.concCost > 0) costHtml += `<span style="color:#c084fc; font-weight:bold;">${scaled.concCost} 집중</span> `;
    if (!costHtml) costHtml = `<span style="color:#22c55e; font-weight:bold;">소모 없음</span>`;

    let badgeText = '즉시 시전';
    let badgeBg = 'rgba(34, 197, 94, 0.15)';
    let badgeBorder = '#22c55e';
    let badgeColor = '#4ade80';

    if (def.isToggle) {
      badgeText = isToggleActive ? '✨ 집중력 버프 (활성화됨)' : '✨ 집중력 버프 (토글)';
      badgeBg = isToggleActive ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.15)';
      badgeBorder = '#a855f7';
      badgeColor = '#c084fc';
    } else if (def.castTime > 0) {
      const actualCast = (def.castTime * metrics.castTimeMult).toFixed(2);
      badgeText = `⏳ ${actualCast}초 시전 (속도보정)`;
      badgeBg = 'rgba(56, 189, 248, 0.15)';
      badgeBorder = '#38bdf8';
      badgeColor = '#38bdf8';
    }

    let cdText = `${scaled.cooldown.toFixed(1)}초`;
    if (cd > 0) {
      cdText += ` <span style="color:#f87171; font-weight:bold;">(⏳ ${cd.toFixed(1)}s)</span>`;
    }

    const bonusLabel = equipBonus > 0 ? `<span style="color:#4ade80;">+${equipBonus} 인챈트</span>` : '';

    this.tooltipEl.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:8px;">
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="font-size:26px; line-height:1;">${def.icon}</span>
          <div>
            <div style="font-size:15px; font-weight:700; color:#f8fafc;">
              ${def.nameKr} <span style="color:#38bdf8; font-size:13px;">Lv.${effLevel}</span>
            </div>
            <div style="font-size:11px; color:#94a3b8;">
              시전단수: Lv.${castLv}/${trainedLv} ${bonusLabel}
            </div>
          </div>
        </div>
        <div style="background:#1e293b; border:1px solid #475569; border-radius:4px; padding:2px 6px; font-size:11px; font-weight:bold; color:#f59e0b;">
          [${slot}] 번 키
        </div>
      </div>

      <div style="display:flex; gap:6px; margin-bottom:8px; flex-wrap:wrap;">
        <span style="background:${badgeBg}; color:${badgeColor}; font-size:10px; font-weight:bold; padding:2px 7px; border-radius:4px; border:1px solid ${badgeBorder};">
          ${badgeText}
        </span>
        <span style="background:rgba(30,41,59,0.8); color:#cbd5e1; font-size:10px; font-weight:600; padding:2px 7px; border-radius:4px; border:1px solid #475569;">
          사거리/반경: ${scaled.range.toFixed(1)}타일
        </span>
      </div>

      <div style="display:grid; grid-template-columns:1fr 1fr; gap:6px; background:rgba(15,23,42,0.7); padding:8px 10px; border-radius:6px; border:1px solid #334155; margin-bottom:8px; font-size:11px;">
        <div><span style="color:#94a3b8;">소모:</span> ${costHtml}</div>
        <div><span style="color:#94a3b8;">쿨다운:</span> ${cdText}</div>
        <div style="grid-column:span 2;"><span style="color:#94a3b8;">위력/수치:</span> <span style="font-weight:bold; color:#fde047;">${powerText}</span> ${scaled.duration > 0 ? `<span style="color:#38bdf8;">(지속 ${scaled.duration}초)</span>` : ''}</div>
      </div>

      <div style="font-size:12px; color:#e2e8f0; line-height:1.5; margin-bottom:6px;">
        ${def.description}
      </div>
      <div style="font-size:11px; color:#fbbf24; background:rgba(245,158,11,0.1); border:1px solid rgba(245,158,11,0.3); padding:5px 8px; border-radius:5px; margin-bottom:8px;">
        📈 <b>성장 공식:</b> ${def.growthSummary}
      </div>

      <div style="display:flex; justify-content:space-between; align-items:center; border-top:1px solid #334155; padding-top:6px; font-size:10px; color:#64748b;">
        <span>🖱️ 좌클릭: 시전 | [C]창에서 스킬레벨/단수 조절</span>
      </div>
    `;

    if (this.tooltipArrowEl) {
      this.tooltipEl.appendChild(this.tooltipArrowEl);
    }
  }

  public update(player: EntityState): void {
    if (!this.containerEl) return;
    this.lastPlayerState = player;

    const { skillSet, maxSlots, roleKey } = this.getEffectiveSkillSet(player);
    const metrics = getDerivedCombatMetrics(player);

    if (this.currentRole !== roleKey) {
      this.currentRole = roleKey;
      for (let slot = 1; slot <= 8; slot++) {
        const s = this.cachedSlots[slot];
        if (!s) continue;
        if (slot > maxSlots || !skillSet[slot]) {
          s.slotEl.style.display = 'none';
          continue;
        }
        s.slotEl.style.display = 'flex';
        const def = skillSet[slot];
        s.iconEl.innerText = def.icon;
        s.nameEl.innerText = def.nameKr;
      }
    }

    for (let slot = 1; slot <= 8; slot++) {
      const s = this.cachedSlots[slot];
      if (!s) continue;
      if (slot > maxSlots || !skillSet[slot]) {
        s.slotEl.style.display = 'none';
        continue;
      }
      s.slotEl.style.display = 'flex';
      const def = skillSet[slot];

      const effLevel = getEffectiveSkillLevel(player, slot);
      const equipBonus = Math.min(10, player.equipSkillBonuses?.[slot] ?? 0);
      const scaled = getScaledSkillMetrics(def, effLevel);
      const actualSp = Math.round(scaled.spCost * metrics.spCostMult);

      s.lvlBadgeEl.innerHTML = equipBonus > 0 ? `Lv.${effLevel}<span style="color:#4ade80;">(+${equipBonus})</span>` : `Lv.${effLevel}`;

      let costText = '';
      if (actualSp > 0) costText += `<span style="color:#eab308;">${actualSp}SP</span> `;
      if (scaled.mpCost > 0) costText += `<span style="color:#38bdf8;">${scaled.mpCost}MP</span>`;
      if (scaled.concCost > 0) costText += `<span style="color:#c084fc;">${scaled.concCost}C</span>`;
      s.costEl.innerHTML = costText;

      const cd = player.cooldowns[slot] || 0;
      const isOnCooldown = cd > 0;

      let isToggleActive = false;
      if (def.isToggle) {
        isToggleActive = player.buffs.some((b) => b.id.includes(`_${roleKey}_${slot}`) || b.id.includes(`_${player.role}_${slot}`));
      }

      const hasSp = player.resources.sp >= actualSp;
      const hasMp = player.resources.mp >= scaled.mpCost;
      const canCast = hasSp && hasMp && !isOnCooldown;

      const borderColor = isToggleActive ? '#22c55e' : canCast ? '#475569' : '#b91c1c';
      const slotBg = isToggleActive ? 'rgba(34, 197, 94, 0.22)' : 'rgba(30, 41, 59, 0.75)';

      s.slotEl.style.borderColor = borderColor;
      s.slotEl.style.backgroundColor = slotBg;

      if (isOnCooldown) {
        s.cdOverlayEl.style.display = 'flex';
        s.cdOverlayEl.innerText = `${cd.toFixed(1)}s`;
      } else {
        s.cdOverlayEl.style.display = 'none';
      }
    }

    if (this.activeTooltipSlot !== null && this.tooltipEl && this.tooltipEl.style.display === 'block') {
      this.renderTooltipContent(this.activeTooltipSlot);
    }
  }
}
